import { useEffect, useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Users, Award, Calendar, FileCheck, TrendingUp } from "lucide-react";

const stats = [
  {
    icon: <Users className="w-8 h-8 text-primary" />,
    value: 10000,
    suffix: "+",
    label: "Satisfied Clients",
    description: "Businesses and individuals across Australia",
  },
  {
    icon: <Calendar className="w-8 h-8 text-primary" />,
    value: 15,
    suffix: "+",
    label: "Years of Experience",
    description: "Providing expert financial services",
  },
  {
    icon: <FileCheck className="w-8 h-8 text-primary" />,
    value: 25000,
    suffix: "+",
    label: "Tax Returns Filed",
    description: "Helping clients maximize their refunds",
  },
  {
    icon: <Award className="w-8 h-8 text-primary" />,
    value: 98,
    suffix: "%",
    label: "Client Satisfaction",
    description: "Based on our client feedback surveys",
  },
];

const Counter = ({ value, suffix, duration = 2000 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    let start = 0;
    const end = parseInt(value.toString().substring(0, 3));

    if (!isInView) return;

    // Get the full value length to determine if we need to count by 1s, 10s, or 100s
    const valueStr = value.toString();
    const increment =
      valueStr.length > 3 ? Math.pow(10, valueStr.length - 3) : 1;

    // Don't want a duration that's too long for large numbers
    const adjustedDuration = valueStr.length > 4 ? duration / 2 : duration;

    // Calculate the time to increment the counter
    const timer = setInterval(() => {
      start += increment;
      setCount(start);
      if (start >= value) {
        setCount(value);
        clearInterval(timer);
      }
    }, adjustedDuration / (value / increment));

    return () => clearInterval(timer);
  }, [value, duration, isInView]);

  return (
    <span ref={ref}>
      {isInView ? count : 0}
      {suffix}
    </span>
  );
};

const Statistics = () => {
  return (
    <section className="py-24 bg-[url('/assets/dot-pattern.svg')] dark:bg-gray-900 dark:bg-[url('/assets/dot-pattern-dark.svg')] relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary/20 to-transparent"></div>
      <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary/20 to-transparent"></div>

      <div className="absolute -top-20 -left-20 w-40 h-40 bg-primary/5 dark:bg-primary/10 rounded-full blur-3xl"></div>
      <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-primary/5 dark:bg-primary/10 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-4xl font-bold mb-5 text-foreground dark:text-white">
              Our Impact in Numbers
            </h2>
            <div className="w-20 h-1 bg-primary mx-auto mb-5"></div>
            <p className="text-muted-foreground dark:text-gray-300 max-w-2xl mx-auto text-lg">
              Delivering exceptional financial services across Australia
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-card dark:bg-gray-800/80 rounded-xl p-8 shadow-lg border border-border dark:border-gray-700 text-center hover:shadow-xl transition-all duration-300 hover:translate-y-[-5px]"
            >
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 dark:bg-primary/20 mb-6 mx-auto">
                {stat.icon}
              </div>

              <h3 className="text-4xl font-bold mb-2 text-foreground dark:text-white">
                <Counter value={stat.value} suffix={stat.suffix} />
              </h3>

              <h4 className="text-xl font-semibold mb-2 text-foreground dark:text-white">
                {stat.label}
              </h4>

              <p className="text-muted-foreground dark:text-gray-300">
                {stat.description}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="mt-16 bg-card dark:bg-gray-800/80 rounded-xl p-8 shadow-lg border border-border dark:border-gray-700"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-foreground dark:text-white">
                  Growing Every Day
                </h3>
                <p className="text-muted-foreground dark:text-gray-300">
                  Join our expanding network of satisfied clients
                </p>
              </div>
            </div>

            <div className="h-16 w-px bg-border dark:bg-gray-700 hidden md:block"></div>

            <div className="text-center md:text-right">
              <p className="text-muted-foreground dark:text-gray-300 mb-2">
                Our client base has grown by{" "}
                <span className="text-primary font-semibold">35%</span> in the
                last year alone
              </p>
              <div className="w-full bg-muted dark:bg-gray-700 h-2 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  whileInView={{ width: "75%" }}
                  transition={{ duration: 1, delay: 0.8 }}
                  className="h-full bg-primary"
                ></motion.div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Statistics;
